import { MenuItem } from "prosemirror-menu";
import { type EditorState, type Selection, TextSelection } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { schema } from "../adapter/schema";
import { linkIconSpec } from "../icons";
import { UPDATE_POPOVER_CLOSE_META } from "../plugins/link-popover";
import {
  addLinkMark,
  isFormulaSelected,
  isImageSelected,
  isSelectionInCodeBlock,
  markActive,
  transformLinkUrl,
  validateLinkUrl,
} from "../utils";

import { TextField, createSelectionElement, openPopover, openPopoverForm } from "./popover";

import { t } from "i18n/i18n";
import { rollbarAndLogError } from "utils/logger";

let isCreateFormOpen = false;

/** Used by link-popover plugin to avoid opening existing-link popover over the create form */
export function isLinkCreateFormOpen(): boolean {
  return isCreateFormOpen;
}

/** Expands an empty selection to include the full link node if the cursor is inside a link mark. */
export function expandSelectionForLink(state: EditorState): Selection | null {
  if (!state.selection.empty) return state.selection;

  // If the selection is empty, we need to treat the link node as the selection
  const node = state.doc.nodeAt(state.selection.from);
  if (node && node.marks.some((mark) => mark.type === schema.marks.link)) {
    const $pos = state.selection.$anchor;
    const from = $pos.pos - $pos.textOffset;
    return TextSelection.create(state.doc, from, from + node.nodeSize);
  }

  return null;
}

/** Returns a normalized href when the current non-empty selection is a valid URL. */
export function getLinkHrefFromSelection(state: EditorState): string | null {
  if (state.selection.empty) return null;

  const selectedText = state.doc.textBetween(state.selection.from, state.selection.to).trim();
  if (!selectedText.includes(".")) return null;

  const href = transformLinkUrl(selectedText);
  if (validateLinkUrl(href) !== null) return null;
  return href;
}

/** Opens the popover form for creating or editing a link. */
export function openLinkPopoverForm(view: EditorView, initialValue: string | null = null): void {
  const state = view.state;
  const selection = expandSelectionForLink(state) ?? state.selection;
  const selectionEl = createSelectionElement(view, selection);

  isCreateFormOpen = true;
  openPopoverForm({
    appendTo: view.dom.closest(".prosemirror-editor-wrapper") as HTMLElement,
    selectionEl,
    title: `${t("worksheet.rich_text_editor.create_link")}:`,
    onClose: () => {
      isCreateFormOpen = false;
      view.focus();
      // After form closes, show the popover for the existing link if the cursor is still in a link
      setTimeout(() => {
        const currentState = view.state;
        if (markActive(currentState, schema.marks.link) && currentState.selection.empty) {
          const close = openLinkPopoverForExistingLink(view);
          // Update the plugin's close function reference
          view.dispatch(currentState.tr.setMeta(UPDATE_POPOVER_CLOSE_META, close));
        }
      }, 0);
    },
    fields: {
      href: new TextField({
        name: "href",
        value: initialValue ?? undefined,
        label: t("worksheet.rich_text_editor.link_target"),
        placeholder: "https://example.com",
        required: true,
        transform: transformLinkUrl,
        validate: validateLinkUrl,
      }),
    },
    callback(attrs) {
      const currentState = view.state;
      const currentSelection = expandSelectionForLink(currentState) ?? currentState.selection;
      const { from, to } = currentSelection;
      view.dispatch(addLinkMark(currentState.tr, from, to, String(attrs.href)).scrollIntoView());

      view.focus();
    },
  });
}

/**
 * Opens the link popover for viewing/editing an existing link
 * @returns A function to close the popover, or null if it couldn't be opened (e.g. no link at cursor)
 */
export function openLinkPopoverForExistingLink(view: EditorView): (() => void) | null {
  const state = view.state;
  const selection = expandSelectionForLink(state);
  if (!selection) return null;

  let hrefString: string | null = null;
  state.doc.nodesBetween(selection.from, selection.to, (node) => {
    const mark = node.marks.find((mk) => mk.type === schema.marks.link);
    if (mark && typeof mark.attrs.href === "string") hrefString = mark.attrs.href;
  });
  const link = document.createElement("a");
  link.className =
    "block w-[350px] max-w-full truncate rounded bg-black-tint-97 px-3 py-2 text-base text-blue underline";
  if (hrefString) {
    link.href = hrefString;
    link.textContent = hrefString;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
  }

  const selectionEl = createSelectionElement(view, selection);

  let closeFn: (() => void) | null = null;

  openPopover({
    appendTo: view.dom.closest(".prosemirror-editor-wrapper") as HTMLElement,
    selectionEl,
    onClose: () => view.focus(),
    render: (close) => {
      closeFn = close;

      const wrapper = document.createElement("div");
      wrapper.className = "flex w-full flex-col gap-4";
      const fields = document.createElement("div");
      fields.className = "flex w-full flex-col gap-2";
      wrapper.appendChild(fields);

      const addFields = () => {
        const title = document.createElement("h3");
        title.textContent = `${t("worksheet.rich_text_editor.visit_link")}:`;
        fields.appendChild(title);
        if (hrefString) {
          const fieldWrapper = document.createElement("div");
          fieldWrapper.className = "w-full";
          fieldWrapper.appendChild(link);
          fields.appendChild(fieldWrapper);
        } else {
          rollbarAndLogError("[ProseMirrorEditor] Link mark is active but has no href attribute");
        }
      };

      const addButtons = () => {
        const buttons = document.createElement("div");
        buttons.className = "flex flex-row items-center justify-between";
        wrapper.appendChild(buttons);
        const removeButton = document.createElement("button");
        removeButton.className = "popover-button popover-button-cancel";
        removeButton.textContent = t("common.remove");
        removeButton.onclick = () => {
          const currentState = view.state;
          const currentSelection = expandSelectionForLink(currentState) ?? currentState.selection;
          view.dispatch(
            currentState.tr
              .removeMark(currentSelection.from, currentSelection.to, schema.marks.link)
              .scrollIntoView(),
          );
          close();
        };
        buttons.appendChild(removeButton);
        const editButton = document.createElement("button");
        editButton.className = "popover-button popover-button-submit";
        editButton.textContent = t("common.edit");
        editButton.onclick = () => {
          close();
          openLinkPopoverForm(view, hrefString);
        };
        buttons.appendChild(editButton);
      };

      addFields();
      addButtons();

      return wrapper;
    },
  });

  return closeFn;
}

/** Creates a toolbar MenuItem that toggles the link popover or removes the link mark. */
export function linkItem() {
  return new MenuItem({
    title: t("worksheet.rich_text_editor.manage_link"),
    icon: linkIconSpec,
    active(state) {
      return markActive(state, schema.marks.link);
    },
    enable(state) {
      if (isSelectionInCodeBlock(state) || isFormulaSelected(state) || isImageSelected(state))
        return false;
      if (!state.selection.empty) return true;

      // If selection is empty, only enable if the cursor is inside a link mark
      let isInLink = false;
      state.doc.nodesBetween(state.selection.from, state.selection.to, (node) => {
        if (node.marks.some((mark) => mark.type === schema.marks.link)) {
          isInLink = true;
        }
      });
      return isInLink;
    },
    run(state, dispatch, view) {
      if (markActive(state, schema.marks.link)) {
        // If link is active and selection is not empty, remove the link
        if (!state.selection.empty) {
          const linkSelection = expandSelectionForLink(state) ?? state.selection;
          dispatch(
            state.tr
              .removeMark(linkSelection.from, linkSelection.to, schema.marks.link)
              .scrollIntoView(),
          );
          view.focus();
        } else {
          // If link is active but selection is empty, keep the popover open
          const close = openLinkPopoverForExistingLink(view);
          view.dispatch(state.tr.setMeta(UPDATE_POPOVER_CLOSE_META, close));
        }
      } else {
        const href = getLinkHrefFromSelection(state);
        if (href) {
          const { from, to } = state.selection;
          dispatch(addLinkMark(state.tr, from, to, href).scrollIntoView());
          view.focus();
        } else {
          // If the selection is not a URL, open the form to create a link
          openLinkPopoverForm(view);
        }
      }
    },
  });
}
